import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react'
import { Card, Table, Skeleton } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFile } from '@fortawesome/free-solid-svg-icons'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import type { TableColumn } from '../../../ui_configs/table_grid.ts'
import type {
  TableViewProps,
  SortConfig,
  FilterConfig,
  FilterLocalState,
  RangeFilterValue,
  DateRangeFilterValue
} from './TableViewTypes'

import StandardEditCell from './parts/StandardEditCell.tsx'
import TextFilterDropdown, { TextFilterIcon } from './parts/TextFilterDropdown.tsx'
import MultiselectFilterDropdown, { MultiselectFilterIcon } from './parts/MultiselectFilterDropdown.tsx'
import RangeFilterDropdown, { RangeFilterIcon } from './parts/RangeFilterDropdown.tsx'
import DateRangeFilterDropdown, { DateRangeFilterIcon } from './parts/DateRangeFilterDropdown.tsx'
import CustomFilterDropdown, { CustomFilterIcon } from './parts/CustomFilterDropdown.tsx'
import ScrollToTopButton from './parts/ScrollToTopButton.tsx'
import { processColumnBase } from './utils/processColumnBase.tsx'
import { recordHasTotalLabel } from '../../utils/recordRowBold.ts'

const EMPTY_FILTERS: FilterConfig = {}

/** A placeholder row for the loading state; only its key and id are ever read. */
type SkeletonRow = { key: string; id: string }

export default function TableView<T extends { id: string | number } = any>({
  headerActions,
  headerStyle,
  columns = [],
  dataSource = [],
  onLoadMore,
  loading = false,
  loadingMore = false,
  hasMore = true,
  currentPage = 1,
  pageSize = 200,
  rowHeight = 40,
  onFiltersChange,
  onSortChange,
  filterOptions = {},
  filterOptionsLoading = false,
  currentSortConfig = null,
  currentFilters = EMPTY_FILTERS,
  defaultSortColumn,
  enableCellEditing = false,
  onCellSave,
  frozenColumns = [],
  rowClassName: rowClassNameProp,
  rowSelection,
  totalsMode = 'inline',
  showTotal = true,
  isTotalRow
}: TableViewProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const tableMeasureRef = useRef<HTMLDivElement>(null)
  const scrollElementRef = useRef<HTMLElement | null>(null)
  const [bodyScrollHeight, setBodyScrollHeight] = useState<number>(400)
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false)
  const [isLoadingNextBatch, setIsLoadingNextBatch] = useState<boolean>(false)

  const [editingCell, setEditingCell] = useState<string | null>(null)
  const [editingCellData, setEditingCellData] = useState<string>('')

  const [filterLocalState, setFilterLocalState] = useState<FilterLocalState>({})
  const syncedFilterKeys = useRef<Set<string>>(new Set())

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [isResizing, setIsResizing] = useState<boolean>(false)
  const [resizingColumn, setResizingColumn] = useState<string | null>(null)
  const [justFinishedResizing, setJustFinishedResizing] = useState<boolean>(false)
  const resizeStartX = useRef<number | null>(null)
  const resizeStartWidth = useRef<number | null>(null)
  const hasResized = useRef<boolean>(false)

  const COLUMN_EXPANSION_FACTOR = 1.5
  const [editingColumnWidths, setEditingColumnWidths] = useState<Record<string, number>>({})
  const [originalWidthsBackup, setOriginalWidthsBackup] = useState<Record<string, number>>({})
  const [wasWidthOriginallySet, setWasWidthOriginallySet] = useState<Record<string, boolean>>({})

  const restoreColumnWidth = useCallback(
    (columnKey: string) => {
      if (columnKey && editingColumnWidths[columnKey]) {
        if (wasWidthOriginallySet[columnKey]) {
          const expandedWidth = editingColumnWidths[columnKey]

          const storedOriginalWidth = originalWidthsBackup[columnKey]
          const calculatedOriginal = expandedWidth / COLUMN_EXPANSION_FACTOR
          const originalWidth = storedOriginalWidth || Math.round(calculatedOriginal)

          setColumnWidths((prev) => ({
            ...prev,
            [columnKey]: originalWidth
          }))
        }

        setEditingColumnWidths((prev) => {
          const newState = { ...prev }
          delete newState[columnKey]
          return newState
        })

        setOriginalWidthsBackup((prev) => {
          const newState = { ...prev }
          delete newState[columnKey]
          return newState
        })
        setWasWidthOriginallySet((prev) => {
          const newState = { ...prev }
          delete newState[columnKey]
          return newState
        })
      }
    },
    [editingColumnWidths, originalWidthsBackup, wasWidthOriginallySet, COLUMN_EXPANSION_FACTOR]
  )

  const handleCellEdit = useCallback(
    (recordId: string | number, columnKey: string, currentValue: any) => {
      if (enableCellEditing) {
        const cellKey = `${recordId}-${columnKey}`
        setEditingCell(cellKey)
        setEditingCellData(currentValue || '')

        if (!editingColumnWidths[columnKey]) {
          const widthValue =
            columnWidths[columnKey] || columns.find((col) => (col.dataIndex || col.key) === columnKey)?.width
          const currentWidth: number = typeof widthValue === 'number' ? widthValue : 150
          const expandedWidth = Math.round(currentWidth * COLUMN_EXPANSION_FACTOR)

          const wasOriginallySet = columnWidths.hasOwnProperty(columnKey)

          setOriginalWidthsBackup((prev) => ({
            ...prev,
            [columnKey]: currentWidth
          }))

          setWasWidthOriginallySet((prev) => ({
            ...prev,
            [columnKey]: wasOriginallySet
          }))

          setEditingColumnWidths((prev) => ({
            ...prev,
            [columnKey]: expandedWidth
          }))
        }
      }
    },
    [enableCellEditing, columnWidths, editingColumnWidths, COLUMN_EXPANSION_FACTOR, columns]
  )

  const handleCellSave = useCallback(
    async (recordId: string | number, columnKey: string) => {
      if (onCellSave) {
        const currentEditingCell = editingCell
        const currentEditingCellData = editingCellData

        setEditingCell(null)
        setEditingCellData('')
        restoreColumnWidth(columnKey)

        try {
          const result = await onCellSave(recordId, columnKey, currentEditingCellData)
          if (result === false) {
            setEditingCell(currentEditingCell)
            setEditingCellData(currentEditingCellData)
            if (enableCellEditing) {
              const widthValue =
                columnWidths[columnKey] || columns.find((col) => (col.dataIndex || col.key) === columnKey)?.width
              const currentWidth: number = typeof widthValue === 'number' ? widthValue : 150
              const expandedWidth = Math.round(currentWidth * COLUMN_EXPANSION_FACTOR)

              const wasOriginallySet = columnWidths.hasOwnProperty(columnKey)

              setOriginalWidthsBackup((prev) => ({
                ...prev,
                [columnKey]: currentWidth
              }))

              setWasWidthOriginallySet((prev) => ({
                ...prev,
                [columnKey]: wasOriginallySet
              }))

              setEditingColumnWidths((prev) => ({
                ...prev,
                [columnKey]: expandedWidth
              }))
            }
          }
        } catch (error) {
          console.error(`Error in cell save for ${columnKey}:`, error)
          setEditingCell(currentEditingCell)
          setEditingCellData(currentEditingCellData)
          if (enableCellEditing) {
            const widthValue =
              columnWidths[columnKey] || columns.find((col) => (col.dataIndex || col.key) === columnKey)?.width
            const currentWidth: number = typeof widthValue === 'number' ? widthValue : 150
            const expandedWidth = Math.round(currentWidth * COLUMN_EXPANSION_FACTOR)

            setOriginalWidthsBackup((prev) => ({
              ...prev,
              [columnKey]: currentWidth
            }))

            setEditingColumnWidths((prev) => ({
              ...prev,
              [columnKey]: expandedWidth
            }))
          }
        }
      } else {
        setEditingCell(null)
        setEditingCellData('')

        restoreColumnWidth(columnKey)
      }
    },
    [
      onCellSave,
      editingCell,
      editingCellData,
      restoreColumnWidth,
      enableCellEditing,
      columnWidths,
      columns,
      COLUMN_EXPANSION_FACTOR,
      setOriginalWidthsBackup,
      setEditingColumnWidths
    ]
  )

  const handleCellCancel = useCallback(() => {
    if (editingCell) {
      const lastDashIndex = editingCell.lastIndexOf('-')
      if (lastDashIndex !== -1) {
        const columnKey = editingCell.substring(lastDashIndex + 1)

        setEditingCell(null)
        setEditingCellData('')

        restoreColumnWidth(columnKey)
      }
    } else {
      setEditingCell(null)
      setEditingCellData('')
    }
  }, [editingCell, restoreColumnWidth])

  const handleCellDataChange = useCallback((value: any) => {
    setEditingCellData(value)
  }, [])

  const handleFieldEdit = useCallback(
    (columnKey: string, isEditing: boolean) => {
      if (isEditing) {
        if (!editingColumnWidths[columnKey] && columnKey !== DealFields.accounts__description) {
          const columnDefByKey = columns.find((col) => col.key === columnKey)
          const columnDefByDataIndex = columns.find((col) => col.dataIndex === columnKey)
          const columnDef = columnDefByKey || columnDefByDataIndex
          const columnDefWidthValue = columnDef?.width
          const columnDefWidth: number = typeof columnDefWidthValue === 'number' ? columnDefWidthValue : 150

          const wasOriginallySetValue = wasWidthOriginallySet[columnKey]
          let currentWidth: number
          let isCurrentlySet: boolean

          if (wasOriginallySetValue === true) {
            const widthValue = columnWidths[columnKey]
            currentWidth = typeof widthValue === 'number' ? widthValue : columnDefWidth
            isCurrentlySet = true
          } else if (wasOriginallySetValue === false) {
            currentWidth = columnDefWidth
            isCurrentlySet = false
          } else {
            if (columnWidths.hasOwnProperty(columnKey)) {
              const widthValue = columnWidths[columnKey]
              currentWidth = typeof widthValue === 'number' ? widthValue : columnDefWidth
              isCurrentlySet = true
            } else {
              currentWidth = columnDefWidth
              isCurrentlySet = false
            }
          }

          const expandedWidth = Math.round(currentWidth * COLUMN_EXPANSION_FACTOR)

          setOriginalWidthsBackup((prev) => ({
            ...prev,
            [columnKey]: currentWidth
          }))

          setWasWidthOriginallySet((prev) => ({
            ...prev,
            [columnKey]: isCurrentlySet
          }))

          setEditingColumnWidths((prev) => ({
            ...prev,
            [columnKey]: expandedWidth
          }))
        }
      } else {
        if (editingColumnWidths[columnKey]) {
          restoreColumnWidth(columnKey)
        }
      }
    },
    [editingColumnWidths, columnWidths, columns, COLUMN_EXPANSION_FACTOR, restoreColumnWidth, wasWidthOriginallySet]
  )

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, columnKey: string) => {
      e.preventDefault()
      e.stopPropagation()
      setIsResizing(true)
      setResizingColumn(columnKey)
      resizeStartX.current = e.clientX
      resizeStartWidth.current = columnWidths[columnKey] || 150
      hasResized.current = false
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [columnWidths]
  )

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !resizingColumn || resizeStartX.current === null) return

      const deltaX = e.clientX - resizeStartX.current
      const newWidth = Math.max(50, (resizeStartWidth.current || 0) + deltaX)

      if (Math.abs(deltaX) > 2) {
        hasResized.current = true
      }

      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: newWidth
      }))
    },
    [isResizing, resizingColumn]
  )

  const handleResizeEnd = useCallback(() => {
    const wasResized = hasResized.current
    setIsResizing(false)
    setResizingColumn(null)
    resizeStartX.current = null
    resizeStartWidth.current = null
    document.body.style.cursor = ''
    document.body.style.userSelect = ''

    if (wasResized) {
      setJustFinishedResizing(true)
      setTimeout(() => {
        setJustFinishedResizing(false)
      }, 150)
    }
  }, [])

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
      return () => {
        document.removeEventListener('mousemove', handleResizeMove)
        document.removeEventListener('mouseup', handleResizeEnd)
      }
    }
    return undefined
  }, [isResizing, handleResizeMove, handleResizeEnd])

  const renderStandardEditCell = useCallback(
    (
      column: TableColumn<T>,
      item: T,
      value: any,
      onChange: (value: any) => void,
      onSave: () => void,
      onCancel: () => void
    ) => {
      return (
        <StandardEditCell
          column={column}
          item={item}
          value={value}
          onChange={onChange}
          onSave={onSave}
          onCancel={onCancel}
        />
      )
    },
    []
  )

  const processedColumns = useMemo(() => {
    return columns.map((column, columnIndex) => {
      const columnKey = column.key as string
      const actualFilterKey = column.filterKey || columnKey

      const columnWithSort = processColumnBase({
        column,
        columnIndex,
        columnKey,
        columns,
        columnWidths,
        editingColumnWidths,
        frozenColumns,
        currentSortConfig,
        currentFilters,
        handleResizeStart,
        editingCell,
        editingCellData,
        enableCellEditing,
        handleCellEdit,
        handleCellSave,
        handleCellCancel,
        handleCellDataChange,
        handleFieldEdit,
        renderStandardEditCell
      })
      const columnWithoutHeaderSort: TableColumn<T> & { sorter: false } = {
        ...columnWithSort,
        // Sorting is managed via ViewConfig drawer; hide header sorter affordances.
        sorter: false,
        sortOrder: undefined,
        showSorterTooltip: false
      }

      if (!column.filterDropdown) return columnWithoutHeaderSort

      if (column.filterType === 'text' || !column.filterType) {
        return {
          ...columnWithoutHeaderSort,
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <TextFilterDropdown
              columnKey={columnKey}
              columnTitle={column.title as string}
              actualFilterKey={actualFilterKey}
              filterLocalState={filterLocalState}
              setFilterLocalState={setFilterLocalState}
              syncedFilterKeys={syncedFilterKeys}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              confirm={confirm}
              onFiltersChange={onFiltersChange}
            />
          ),
          filterIcon: TextFilterIcon
        }
      }

      if (column.filterType === 'multiselect') {
        return {
          ...columnWithoutHeaderSort,
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <MultiselectFilterDropdown
              columnKey={columnKey}
              columnTitle={column.title as string}
              actualFilterKey={actualFilterKey}
              filterLocalState={filterLocalState}
              setFilterLocalState={setFilterLocalState}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              confirm={confirm}
              filterOptions={filterOptions}
              filterOptionsLoading={filterOptionsLoading}
              onFiltersChange={onFiltersChange}
            />
          ),
          filterIcon: MultiselectFilterIcon
        }
      }

      if (column.filterType === 'range') {
        return {
          ...columnWithoutHeaderSort,
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <RangeFilterDropdown
              columnKey={columnKey}
              columnFieldType={(column as any).fieldType}
              actualFilterKey={actualFilterKey}
              filterLocalState={filterLocalState}
              setFilterLocalState={setFilterLocalState}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              confirm={confirm}
              onFiltersChange={onFiltersChange}
            />
          ),
          filterIcon: RangeFilterIcon
        }
      }

      if (column.filterType === 'date_range') {
        return {
          ...columnWithoutHeaderSort,
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <DateRangeFilterDropdown
              columnKey={columnKey}
              actualFilterKey={actualFilterKey}
              filterLocalState={filterLocalState}
              setFilterLocalState={setFilterLocalState}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              confirm={confirm}
              onFiltersChange={onFiltersChange}
            />
          ),
          filterIcon: DateRangeFilterIcon
        }
      }

      if (column.filterType === 'custom' && column.customFilterComponent) {
        return {
          ...columnWithoutHeaderSort,
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <CustomFilterDropdown
              columnKey={columnKey}
              actualFilterKey={actualFilterKey}
              CustomFilterComponent={column.customFilterComponent!}
              selectedKeys={selectedKeys}
              setSelectedKeys={setSelectedKeys}
              confirm={confirm}
              clearFilters={clearFilters}
              onFiltersChange={onFiltersChange}
            />
          ),
          filterIcon: CustomFilterIcon
        }
      }

      return columnWithoutHeaderSort
    })
  }, [
    columns,
    columnWidths,
    editingColumnWidths,
    filterOptions,
    filterOptionsLoading,
    currentSortConfig,
    currentFilters,
    editingCell,
    editingCellData,
    enableCellEditing,
    handleCellEdit,
    handleCellSave,
    handleCellCancel,
    handleCellDataChange,
    renderStandardEditCell,
    handleResizeStart,
    frozenColumns,
    filterLocalState,
    onFiltersChange,
    handleFieldEdit
  ])

  // Split dataSource into data rows and total rows based on the configured
  // predicate (default: recordHasTotalLabel — the standard helper used across
  // the app). When `showTotal` is false, totals are dropped entirely. When
  // `totalsMode === 'sticky-bottom'`, totals are pulled out of the body and
  // rendered in AntD's `<Table.Summary fixed>` slot at the bottom of the
  // viewport. Defined above the layout effect because that effect depends on
  // `totalRows.length`.
  const isTotal = isTotalRow ?? ((r: T) => recordHasTotalLabel(r as unknown as Record<string, unknown>))
  const { dataRows, totalRows } = useMemo(() => {
    if (!dataSource || dataSource.length === 0) return { dataRows: [] as T[], totalRows: [] as T[] }
    if (!showTotal) return { dataRows: dataSource.filter((r) => !isTotal(r)), totalRows: [] as T[] }
    if (totalsMode === 'inline') return { dataRows: dataSource, totalRows: [] as T[] }
    const dr: T[] = []
    const tr: T[] = []
    for (const r of dataSource) {
      if (isTotal(r)) tr.push(r)
      else dr.push(r)
    }
    return { dataRows: dr, totalRows: tr }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, showTotal, totalsMode])

  useLayoutEffect(() => {
    const el = tableMeasureRef.current
    if (!el) return

    const measure = () => {
      const h = el.getBoundingClientRect().height
      if (h <= 0) return
      const thead = el.querySelector<HTMLElement>('.ant-table-thead')
      const theadH = thead?.getBoundingClientRect().height ?? 40
      // When `<Table.Summary fixed>` is rendered, it sits at the bottom of the
      // Card body. The scroll viewport must leave room for it; otherwise it
      // gets clipped by the Card's overflow:hidden when data fills the screen.
      const summary = el.querySelector<HTMLElement>('.ant-table-summary')
      const summaryH = summary?.getBoundingClientRect().height ?? 0
      const next = Math.max(Math.floor(h - theadH - summaryH - 1), 100)
      setBodyScrollHeight((prev) => (prev !== next ? next : prev))
    }

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(measure)
    })
    ro.observe(el)
    // Also observe DOM changes inside the table — the summary footer
    // appears/disappears when totalsMode toggles or totals load asynchronously,
    // and ResizeObserver alone wouldn't fire for that.
    const mo = new MutationObserver(() => {
      requestAnimationFrame(measure)
    })
    mo.observe(el, { childList: true, subtree: true })
    measure()

    return () => {
      ro.disconnect()
      mo.disconnect()
    }
  }, [loading, columns.length, dataSource.length, totalRows.length])

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (justFinishedResizing) {
        return
      }

      if (filters) {
        const filterConfig: FilterConfig = {}
        Object.keys(filters).forEach((key) => {
          if ((filters[key] && filters[key].length > 0) || filters[key] === undefined) {
            const column = processedColumns.find((col) => col.key === key || col.dataIndex === key)

            const actualFilterKey = column?.filterKey || key

            if (filters[key] === undefined) {
              filterConfig[actualFilterKey] = undefined
              return
            }

            if (column && (column.filterType === 'text' || (!column.filterType && column.filterDropdown))) {
              filterConfig[actualFilterKey] = filters[key][0]
            } else if (column && column.filterType === 'range') {
              const rangeObj = filters[key][0]
              if (rangeObj && typeof rangeObj === 'object') {
                const cleanRange: RangeFilterValue = {}
                if (rangeObj.min) {
                  cleanRange.min = rangeObj.min
                }
                if (rangeObj.max) {
                  cleanRange.max = rangeObj.max
                }
                if (Object.keys(cleanRange).length > 0) {
                  filterConfig[actualFilterKey] = cleanRange
                }
              }
            } else if (column && column.filterType === 'date_range') {
              const dateRangeObj = filters[key][0]
              if (dateRangeObj && typeof dateRangeObj === 'object') {
                const cleanDateRange: DateRangeFilterValue = {}
                if (dateRangeObj.min) {
                  cleanDateRange.min = dateRangeObj.min
                }
                if (dateRangeObj.max) {
                  cleanDateRange.max = dateRangeObj.max
                }
                if (Object.keys(cleanDateRange).length > 0) {
                  filterConfig[actualFilterKey] = cleanDateRange
                }
              }
            } else if (column && column.filterType === 'custom') {
              const customFilter = filters[key][0]
              if (customFilter && typeof customFilter === 'object') {
                if ((customFilter as any).companies && Array.isArray((customFilter as any).companies)) {
                  filterConfig[actualFilterKey] = (customFilter as any).companies
                } else {
                  filterConfig[actualFilterKey] = customFilter
                }
              }
            } else {
              filterConfig[actualFilterKey] = filters[key]
            }
          }
        })
        onFiltersChange?.(filterConfig)
      }
    },
    [justFinishedResizing, processedColumns, onFiltersChange]
  )

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget

      scrollElementRef.current = e.currentTarget

      setShowScrollToTop(scrollTop > 200)

      const distanceFromBottom = scrollHeight - scrollTop - clientHeight
      const threshold = 150 * rowHeight

      if (!hasMore || loadingMore || isLoadingNextBatch) return

      if (distanceFromBottom < threshold) {
        setIsLoadingNextBatch(true)
        // onLoadMore may return void; the flag resets either way.
        Promise.resolve(onLoadMore?.(currentPage + 1, pageSize)).finally(() => {
          setIsLoadingNextBatch(false)
        })
      }
    },
    [hasMore, loadingMore, isLoadingNextBatch, currentPage, pageSize, onLoadMore, rowHeight]
  )

  useEffect(() => {
    if (currentFilters && Object.keys(currentFilters).length === 0) {
      setFilterLocalState({})
    }
  }, [currentFilters])

  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      const scrollableElements = [
        containerRef.current.querySelector('.ant-table-tbody-virtual'),
        containerRef.current.querySelector('.ant-table-tbody'),
        containerRef.current.querySelector('.ant-table-body'),
        scrollElementRef.current,
        containerRef.current
      ].filter(Boolean) as HTMLElement[]

      scrollableElements.forEach((element) => {
        try {
          if (element.scrollTo) {
            element.scrollTo({ top: 0, behavior: 'smooth' })
          }
          if (element.scrollTop !== undefined) {
            element.scrollTop = 0
          }
        } catch (error) {}
      })
    }
  }, [])

  const shouldUseVirtual = dataRows && dataRows.length > 100
  const viewportHeight = bodyScrollHeight

  // The loading state renders placeholder rows, which are not T, through a table of their own type;
  // its columns keep the header (title, width, fixed, filter icon) and swap the cell for a skeleton.
  const skeletonColumns = useMemo<ColumnsType<SkeletonRow>>(() => {
    return processedColumns.map((column) => ({
      key: column.key,
      title: typeof column.title === 'function' ? undefined : column.title,
      width: column.width,
      fixed: column.fixed,
      align: column.align,
      ellipsis: column.ellipsis,
      filterDropdown: column.filterDropdown,
      filterIcon: column.filterIcon,
      render: () => (
        <Skeleton.Input
          active
          size='small'
          style={{
            width: '100%',
            height: '16px',
            maxWidth: '200px'
          }}
        />
      )
    }))
  }, [processedColumns])

  const skeletonRows = useMemo<SkeletonRow[]>(() => {
    const skeletonCount = Math.ceil(viewportHeight / rowHeight) || 10
    return Array.from({ length: skeletonCount }, (_, index) => ({
      key: `skeleton-${index}`,
      id: `skeleton-${index}`
    }))
  }, [viewportHeight, rowHeight])

  return (
    <>
      <Card
        className='flex-1 min-h-0 flex flex-col m-0 border border-gray-200 rounded-lg overflow-hidden shadow-sm h-full table-advanced'
        ref={containerRef}
        extra={headerActions}
        styles={{
          ...(headerStyle ? { header: headerStyle } : {}),
          body: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <div ref={tableMeasureRef} className='flex-1 min-h-0 min-w-0 flex flex-col'>
          {loading ? (
            <Table<SkeletonRow>
              dataSource={skeletonRows}
              columns={skeletonColumns}
              loading={false}
              size='small'
              pagination={false}
              rowKey='key'
              virtual={shouldUseVirtual}
              scroll={{ y: viewportHeight, x: 1000 }}
              onScroll={handleScroll}
              rowClassName={(_record, index) => (index % 2 === 0 ? 'table-row-even' : 'table-row-odd')}
            />
          ) : (
          <Table<T>
            dataSource={dataRows}
            columns={processedColumns}
            loading={false}
            size='small'
            pagination={false}
            rowKey='id'
            rowSelection={rowSelection}
            virtual={shouldUseVirtual}
            scroll={{ y: viewportHeight, x: 1000 }}
            onScroll={handleScroll}
            rowClassName={(record, index) => {
              const stripe = index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
              const extra = rowClassNameProp?.(record, index)
              return [stripe, extra].filter(Boolean).join(' ')
            }}
            onChange={handleTableChange}
            summary={
              totalRows.length > 0
                ? () => (
                    <Table.Summary fixed>
                      {totalRows.map((record, ri) => (
                        <Table.Summary.Row
                          key={(record as any).key ?? (record as any).id ?? `total-${ri}`}
                          className={['font-bold', rowClassNameProp?.(record, ri)].filter(Boolean).join(' ')}
                        >
                          {(processedColumns as any[]).map((col, ci) => {
                            const dataIndex = (col.dataIndex ?? col.key) as string
                            const value = (record as any)?.[dataIndex]
                            const rendered = typeof col.render === 'function'
                              ? col.render(value, record, ri, undefined)
                              : value
                            return (
                              <Table.Summary.Cell index={ci} key={col.key ?? dataIndex}>
                                {rendered}
                              </Table.Summary.Cell>
                            )
                          })}
                        </Table.Summary.Row>
                      ))}
                    </Table.Summary>
                  )
                : undefined
            }
            locale={{
              emptyText: (
                <div className='flex flex-col items-center gap-4 py-12'>
                  <div className='text-4xl'>
                    <FontAwesomeIcon icon={faFile} />
                  </div>
                  <div className='text-center'>
                    <h3 className='text-lg font-semibold mb-2'>No data found</h3>
                    <p className='text-gray-600'>There are no records to display at the moment.</p>
                  </div>
                </div>
              )
            }}
          />
          )}
        </div>
      </Card>

      <ScrollToTopButton show={showScrollToTop} onClick={scrollToTop} />
    </>
  )
}

TableView.clearFilters = (onFiltersChange?: (filters: FilterConfig) => void) => {
  onFiltersChange?.({})
}
